/**
 * Intelligence service
 *
 * Orchestration and client-side validation between the controllers and the
 * gateway. Keeps the scope tree in memory (it is small and read on every view)
 * and answers "is this available?" for an organization without the feature.
 */

/** Matches the server's multer limit; checked here so a large file never leaves the browser. */
const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

const SUPPORTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/markdown',
  'text/x-markdown',
  'text/plain',
  'text/csv',
]);

const SUPPORTED_EXTENSIONS = /\.(pdf|docx?|md|markdown|txt|csv)$/i;

/** Browsers report an empty type for Markdown and sometimes CSV, so the name decides then. */
function isSupportedDocument(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type && SUPPORTED_MIME_TYPES.has(type)) return true;
  return SUPPORTED_EXTENSIONS.test(String(file?.name || ''));
}

export class IntelligenceService {
  constructor({ intelligenceGateway, debugGateway } = {}) {
    if (!intelligenceGateway) {
      throw new Error('intelligenceGateway is required');
    }
    this.gateway = intelligenceGateway;
    this.debugGateway = debugGateway || null;
    this._scopes = null;
    this._access = null;
  }

  #debug(message, level) {
    try {
      this.debugGateway?.addMessage?.(message, level || 'info');
    } catch (_) {
      // Diagnostics must never break a flow.
    }
  }

  /** A 404 across this area means the organization does not have the feature. */
  #isUnavailable(error) {
    return error && error.status === 404;
  }

  // ── Scopes ─────────────────────────────────────────────────────────

  async loadScopes({ force = false } = {}) {
    if (this._scopes && !force) return this._scopes;
    try {
      const result = await this.gateway.listScopes();
      if (result?.skipped) {
        // Asked before anyone was signed in. Keep nothing, so the next call
        // asks again once the session is restored.
        this._scopes = null;
        return [];
      }
      this._scopes = Array.isArray(result?.scopes) ? result.scopes : [];
      return this._scopes;
    } catch (error) {
      if (this.#isUnavailable(error)) {
        this._scopes = [];
        return this._scopes;
      }
      this.#debug('[intelligence] failed to load scopes: ' + error.message, 'error');
      throw error;
    }
  }

  getCachedScopes() {
    return this._scopes || [];
  }

  /** True once a real answer has been loaded — an empty organization counts; a skipped fetch does not. */
  hasLoadedScopes() {
    return this._scopes !== null;
  }

  findScope(scopeId) {
    return this.getCachedScopes().find((s) => s.id === scopeId) || null;
  }

  /** The scope linked to an AgilePro team, when the two are bridged. */
  findScopeForTeam(teamId) {
    if (!teamId) return null;
    return this.getCachedScopes().find((s) => s.linkedTeamId === teamId) || null;
  }

  rootScope() {
    return this.getCachedScopes().find((s) => !s.parentId) || null;
  }

  /**
   * Scopes arranged for display: each node with its depth, in tree order.
   * Additional parents are shown on the node rather than duplicating it.
   */
  scopeTree() {
    const scopes = this.getCachedScopes();
    const byParent = new Map();
    scopes.forEach((scope) => {
      const key = scope.parentId || '__root__';
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(scope);
    });
    byParent.forEach((list) => list.sort((a, b) => String(a.name).localeCompare(String(b.name))));

    const ordered = [];
    const walk = (parentKey, depth) => {
      (byParent.get(parentKey) || []).forEach((scope) => {
        ordered.push({ ...scope, depth });
        walk(scope.id, depth + 1);
      });
    };
    walk('__root__', 0);
    return ordered;
  }

  async createScope(input) {
    const name = String(input?.name || '').trim();
    if (!name) throw new Error('A name is required');
    if (name.length > 255) throw new Error('Name must be 255 characters or fewer');
    if (!input?.scopeType) throw new Error('A scope type is required');
    const result = await this.gateway.createScope({ ...input, name });
    await this.loadScopes({ force: true });
    return result;
  }

  async updateScope(scopeId, updates) {
    const next = { ...(updates || {}) };
    if (Object.prototype.hasOwnProperty.call(next, 'name')) {
      next.name = String(next.name || '').trim();
      if (!next.name) throw new Error('A name is required');
      if (next.name.length > 255) throw new Error('Name must be 255 characters or fewer');
    }
    const result = await this.gateway.updateScope(scopeId, next);
    await this.loadScopes({ force: true });
    return result;
  }

  /** Archiving is an update; scopes are never deleted (evidence is filed in them). */
  async archiveScope(scopeId) {
    return this.updateScope(scopeId, { status: 'archived' });
  }

  async addScopeParent(scopeId, parentScopeId) {
    if (!scopeId || !parentScopeId) throw new Error('Choose the parent to add');
    if (scopeId === parentScopeId) throw new Error('A scope cannot sit under itself');
    const result = await this.gateway.addScopeParent(scopeId, parentScopeId);
    await this.loadScopes({ force: true });
    return result;
  }

  async removeScopeParent(scopeId, parentScopeId) {
    const result = await this.gateway.removeScopeParent(scopeId, parentScopeId);
    await this.loadScopes({ force: true });
    return result;
  }

  /**
   * The connections whose evidence lands on this team's scope (spec 013).
   *
   * There is one connection registry, and a team's connections are the ones
   * pointed at the scope linked to that team — no second table, no second kind
   * of connection. A team with no linked scope has none, which is a different
   * answer from "none yet" and the caller says so.
   *
   * @returns {Promise<{ scope: Object|null, connectors: Array }>}
   */
  async connectorsForTeam(teamId) {
    if (!teamId) return { scope: null, connectors: [] };
    await this.loadScopes();
    const scope = this.findScopeForTeam(teamId);
    if (!scope) return { scope: null, connectors: [] };

    const connectors = await this.listConnectors();
    return {
      scope,
      connectors: connectors.filter((connector) => connector.scopeId === scope.id),
    };
  }

  /**
   * Who sees which part of the organization (spec 015).
   *
   * A person granted a scope sees it and everything beneath it, so granting an
   * RTE their ART grants them its teams. `capacity` is a label for why the
   * grant exists — it explains a grant, it does not confer one.
   */
  /**
   * Who holds what at this scope (spec 026).
   *
   * A refusal is thrown rather than swallowed into an empty list: "nobody has
   * a role here" and "you may not see who does" are different facts, and a
   * screen that shows the first when the second is true is lying.
   */
  async listScopeMembers(scopeId) {
    if (!scopeId) return [];
    const result = await this.gateway.listScopeMembers(scopeId);
    return Array.isArray(result?.members) ? result.members : [];
  }

  // ── What this member may do ────────────────────────────────────────

  /**
   * The member's capabilities, read once and kept. `force` after anything
   * that changes them: switching enforcement, or being given a role.
   *
   * An organization without Intelligence answers 404 here and can enforce
   * nothing, so it is treated as allowing everything. Any other failure is
   * treated as allowing nothing: hiding is the safe failure, and the API is
   * still refusing whatever it refuses.
   */
  async loadAccess({ force = false } = {}) {
    if (this._access && !force) return this._access;
    try {
      const result = await this.gateway.myAccess();
      this._access = {
        enforcing: result?.enforcing === true,
        capabilities: Array.isArray(result?.capabilities) ? result.capabilities : [],
        everything: false,
      };
    } catch (error) {
      if (this.#isUnavailable(error)) {
        this._access = { enforcing: false, capabilities: [], everything: true };
      } else {
        this.#debug('[intelligence] failed to load access: ' + error.message, 'error');
        this._access = { enforcing: false, capabilities: [], everything: false };
      }
    }
    return this._access;
  }

  async can(capability) {
    const access = await this.loadAccess();
    return access.everything || access.capabilities.includes(capability);
  }

  async isEnforcing() {
    return (await this.loadAccess()).enforcing;
  }

  /** Everyone in the organization. Refusals are thrown, not flattened. */
  async listOrganizationPeople() {
    const result = await this.gateway.listOrganizationPeople();
    return Array.isArray(result?.people) ? result.people : [];
  }

  /**
   * Remove somebody from the organization. The server refuses to remove you,
   * and refuses to remove the last organization admin; both refusals are thrown
   * in its words.
   */
  async removeMember(userId) {
    if (!userId) throw new Error('Choose who to remove');
    return this.gateway.removeMember(userId);
  }

  // ── Invitations (Settings plan S5) ──────────────────────────────────

  /** Pending invitations. Refusals are thrown, not flattened into an empty list. */
  async listInvitations() {
    const result = await this.gateway.listInvitations();
    return Array.isArray(result?.invitations) ? result.invitations : [];
  }

  async invite({ email, scopeId, role } = {}) {
    if (!email || !scopeId || !role) throw new Error('Enter an email, and choose a place and a role');
    return this.gateway.invite({ email, scopeId, role });
  }

  async revokeInvitation(invitationId) {
    if (!invitationId) throw new Error('Choose an invitation');
    return this.gateway.revokeInvitation(invitationId);
  }

  async resendInvitation(invitationId) {
    if (!invitationId) throw new Error('Choose an invitation');
    return this.gateway.resendInvitation(invitationId);
  }

  async grantCapability(scopeId, { userId, capability, reason = null } = {}) {
    if (!scopeId || !userId || !capability) {
      throw new Error('Choose a scope, a person and what they may do');
    }
    return this.gateway.grantCapability(scopeId, { userId, capability, reason });
  }

  async revokeCapability(scopeId, userId, capability) {
    if (!scopeId || !userId || !capability) {
      throw new Error('Choose a scope, a person and what to take away');
    }
    return this.gateway.revokeCapability(scopeId, userId, capability);
  }

  async assignRole(scopeId, { userId, role } = {}) {
    if (!scopeId) throw new Error('Choose a scope');
    if (!userId) throw new Error('Choose somebody');
    if (!role) throw new Error('Choose a role');
    return this.gateway.assignRole(scopeId, { userId, role });
  }

  async revokeRole(scopeId, userId, role) {
    if (!scopeId || !userId || !role) throw new Error('Choose a scope, a person and a role');
    return this.gateway.revokeRole(scopeId, userId, role);
  }




  /**
   * Turn visibility enforcement on or off for the organization.
   *
   * The scope tree is reloaded afterwards: what the member can see has just
   * changed, and a picker still listing scopes they no longer see would be
   * showing them a door that no longer opens.
   */
  async setScopeVisibility(enforced) {
    const result = await this.gateway.setScopeVisibility(enforced);
    await this.loadScopes({ force: true });
    return result;
  }

  async linkTeam(scopeId, teamId) {
    const result = await this.gateway.linkScopeTeam(scopeId, teamId);
    await this.loadScopes({ force: true });
    return result;
  }

  async unlinkTeam(scopeId) {
    const result = await this.gateway.unlinkScopeTeam(scopeId);
    await this.loadScopes({ force: true });
    return result;
  }

  // ── Entities and relationships ─────────────────────────────────────

  async listEntities(filters) {
    try {
      const result = await this.gateway.listEntities(filters);
      return Array.isArray(result?.entities) ? result.entities : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async createEntity(input) {
    const name = String(input?.name || '').trim();
    if (!name) throw new Error('A name is required');
    if (!input?.entityType) throw new Error('An entity type is required');
    if (!input?.homeScopeId) throw new Error('A scope is required');
    return this.gateway.createEntity({ ...input, name });
  }

  async updateEntity(entityId, updates) {
    const next = { ...(updates || {}) };
    if (Object.prototype.hasOwnProperty.call(next, 'name')) {
      next.name = String(next.name || '').trim();
      if (!next.name) throw new Error('A name is required');
    }
    return this.gateway.updateEntity(entityId, next);
  }

  /**
   * Create an entity and then its aliases, one request each. Alias failures
   * (typically "already used by <other entity>") do not undo the entity; they
   * are reported so the member can see which names did not stick.
   */
  async createEntityWithAliases(input, aliases) {
    const created = await this.createEntity(input);
    const entity = created?.entity || created;
    const name = String(input?.name || '').trim().toLowerCase();

    const seen = new Set();
    const wanted = (aliases || [])
      .map((alias) => String(alias || '').trim())
      .filter((alias) => {
        if (!alias || alias.toLowerCase() === name) return false;
        const key = alias.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    const aliasErrors = [];
    for (const alias of wanted) {
      try {
        await this.gateway.addAlias(entity.id, alias);
      } catch (error) {
        aliasErrors.push(`${alias}: ${error?.message || 'could not be added'}`);
      }
    }
    return { entity, aliasErrors };
  }

  async addAlias(entityId, alias) {
    const value = String(alias || '').trim();
    if (!value) throw new Error('An alias is required');
    return this.gateway.addAlias(entityId, value);
  }

  async removeAlias(entityId, aliasId) {
    return this.gateway.removeAlias(entityId, aliasId);
  }

  async resolveEntities(query) {
    const q = String(query || '').trim();
    if (q.length < 2) return [];
    try {
      const result = await this.gateway.resolveEntities(q);
      return Array.isArray(result?.matches) ? result.matches : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  /**
   * The graph, for the map.
   *
   * Both reads at once, because the map is a function of the two together and
   * fetching them in sequence would draw the nodes a beat before the edges.
   * Neither read is new: this composes what the Delivery System view already
   * uses.
   */
  async deliverySystem(filters) {
    const [entities, relationships] = await Promise.all([
      this.listEntities(filters),
      this.listRelationships(filters),
    ]);
    return { entities, relationships };
  }

  async listRelationships(filters) {
    try {
      const result = await this.gateway.listRelationships(filters);
      return Array.isArray(result?.relationships) ? result.relationships : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async createRelationship(input) {
    const rel = {
      fromKind: 'entity',
      toKind: 'entity',
      ...(input || {}),
    };
    if (!rel.fromId || !rel.toId) throw new Error('Both ends of the relationship are required');
    if (!rel.relType) throw new Error('A relationship type is required');
    if (rel.fromId === rel.toId && rel.fromKind === rel.toKind) {
      throw new Error('A relationship needs two different things');
    }
    return this.gateway.createRelationship(rel);
  }

  async deleteRelationship(relationshipId) {
    return this.gateway.deleteRelationship(relationshipId);
  }

  // ── Evidence ───────────────────────────────────────────────────────

  async listEvidence(filters) {
    try {
      return await this.gateway.listEvidence(filters);
    } catch (error) {
      if (this.#isUnavailable(error)) return { evidence: [], total: 0, nextCursor: null };
      throw error;
    }
  }

  async getEvidence(evidenceId) {
    return this.gateway.getEvidence(evidenceId);
  }

  /**
   * Date inputs give '' for nothing. The API wants null for nothing, and only
   * for the keys that were actually supplied.
   */
  #normaliseDates(input) {
    const next = { ...input };
    ['occurredAt', 'periodStart', 'periodEnd'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(next, key) && !next[key]) next[key] = null;
    });
    return next;
  }

  async createEvidence(input) {
    const title = String(input?.title || '').trim();
    const body = String(input?.body || '').trim();
    if (!input?.evidenceType) throw new Error('An evidence type is required');
    if (!title) throw new Error('A title is required');
    if (title.length > 300) throw new Error('Title must be 300 characters or fewer');
    if (!body) throw new Error('A description is required');
    if (body.length > 20000) throw new Error('Description must be 20,000 characters or fewer');
    if (!input?.scopeId) throw new Error('A scope is required');
    if (!input?.confidence) throw new Error('A confidence level is required');
    if (!input?.occurredAt && !input?.periodStart) {
      throw new Error('Record when this happened, or the period it covers');
    }
    return this.gateway.createEvidence(this.#normaliseDates({ ...input, title, body }));
  }

  /** The same rules as create, applied only to the fields being changed. */
  async updateEvidence(evidenceId, updates) {
    const next = this.#normaliseDates({ ...(updates || {}) });
    const has = (key) => Object.prototype.hasOwnProperty.call(next, key);

    if (has('title')) {
      next.title = String(next.title || '').trim();
      if (!next.title) throw new Error('A title is required');
      if (next.title.length > 300) throw new Error('Title must be 300 characters or fewer');
    }
    if (has('body')) {
      next.body = String(next.body || '').trim();
      if (!next.body) throw new Error('A description is required');
      if (next.body.length > 20000) throw new Error('Description must be 20,000 characters or fewer');
    }
    if (has('evidenceType') && !next.evidenceType) throw new Error('An evidence type is required');
    if (has('scopeId') && !next.scopeId) throw new Error('A scope is required');
    if (has('confidence') && !next.confidence) throw new Error('A confidence level is required');
    if (has('occurredAt') && has('periodStart') && !next.occurredAt && !next.periodStart) {
      throw new Error('Record when this happened, or the period it covers');
    }
    return this.gateway.updateEvidence(evidenceId, next);
  }

  async retractEvidence(evidenceId, reason) {
    const value = String(reason || '').trim();
    if (!value) throw new Error('A reason is required to retract evidence');
    return this.gateway.retractEvidence(evidenceId, value);
  }

  async linkEvidence(evidenceId, link) {
    if (!link?.toEvidenceId) throw new Error('Choose the evidence to link to');
    if (!link?.linkType) throw new Error('Choose how the two are related');
    if (link.toEvidenceId === evidenceId) throw new Error('Evidence cannot link to itself');
    return this.gateway.linkEvidence(evidenceId, link);
  }

  async unlinkEvidence(evidenceId, linkId) {
    return this.gateway.unlinkEvidence(evidenceId, linkId);
  }

  // ── Documents ──────────────────────────────────────────────────────

  async listDocuments(filters) {
    try {
      const result = await this.gateway.listDocuments(filters);
      return Array.isArray(result?.documents) ? result.documents : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async uploadDocument(input) {
    const file = input?.file;
    if (!file) throw new Error('Choose a file to upload');
    if (!input?.scopeId) throw new Error('A scope is required');
    if (Number(file.size) > MAX_DOCUMENT_BYTES) {
      throw new Error('That file is over 25 MB. Split it, or paste the relevant text instead.');
    }
    if (!isSupportedDocument(file)) {
      throw new Error('Upload a PDF, Word, Markdown, text, or CSV file.');
    }
    return this.gateway.uploadDocument(input);
  }

  documentDownloadUrl(documentId) {
    return typeof this.gateway.documentDownloadUrl === 'function'
      ? this.gateway.documentDownloadUrl(documentId)
      : null;
  }

  async extractDocument(documentId) {
    return this.gateway.extractDocument(documentId);
  }

  async pasteDocumentText(documentId, text) {
    const value = String(text || '').trim();
    if (!value) throw new Error('Paste the document text');
    return this.gateway.pasteDocumentText(documentId, value);
  }

  async listSuggestions(documentId, options) {
    const result = await this.gateway.listSuggestions(documentId, options);
    return Array.isArray(result?.suggestions) ? result.suggestions : [];
  }

  async acceptSuggestion(suggestionId, edits) {
    return this.gateway.acceptSuggestion(suggestionId, edits);
  }

  async rejectSuggestion(suggestionId, reason) {
    return this.gateway.rejectSuggestion(suggestionId, reason);
  }

  // ── Ask AgilePro and Attention (spec 004). A conversation is a chat. ──

  /**
   * One question. The API's 409 and 503 answers become errors with a `code`
   * (scope_changed, busy, unavailable) so the controller acts on the code,
   * never on the wording.
   */
  async ask({ question, scopeId, conversationId = null, continueAcrossScope = false } = {}) {
    const text = String(question || '').trim();
    if (!text) throw new Error('Type a question');
    if (text.length > QUESTION_MAX_LENGTH) throw new Error('Questions must be 2,000 characters or fewer');
    if (!scopeId) throw new Error('Choose a scope');

    try {
      return await this.gateway.askQuestion({
        question: text, scopeId, conversationId: conversationId || null, continueAcrossScope: continueAcrossScope === true,
      });
    } catch (error) {
      throw translateAskError(error);
    }
  }

  /** The Attention result for a scope, or null when it cannot be had. */
  async getAttention(scopeId) {
    try {
      const result = await this.gateway.getAttention(scopeId);
      if (!result || result.skipped) return null;
      return result;
    } catch (error) {
      if (this.#isUnavailable(error)) return null;
      this.#debug('[intelligence] failed to load attention: ' + error.message, 'error');
      throw error;
    }
  }

  /** Embedding counts, or null: a hint that cannot be shown is not an error. */
  async getEmbeddingStatus(scopeId) {
    try {
      const result = await this.gateway.getEmbeddingStatus(scopeId);
      if (!result || result.skipped) return null;
      return result;
    } catch (_) {
      return null;
    }
  }

  // ── Investigations (spec 005) ─────────────────────────────────────

  async listInvestigations(filters) {
    try {
      const result = await this.gateway.listInvestigations(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.investigations) ? result.investigations : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async getInvestigation(investigationId) {
    return this.gateway.getInvestigation(investigationId);
  }

  async openInvestigation({ question, scopeId } = {}) {
    const text = String(question || '').trim();
    if (!text) throw new Error('A question is required');
    if (text.length > INVESTIGATION_QUESTION_MAX_LENGTH) throw new Error('Questions must be 500 characters or fewer');
    if (!scopeId) throw new Error('Choose a scope');
    return this.gateway.openInvestigation({ question: text, scopeId });
  }

  async closeInvestigation(investigationId, { status, conclusion } = {}) {
    if (!CLOSING_STATUSES.includes(status)) throw new Error('Choose whether it was answered or abandoned');
    const text = String(conclusion || '').trim();
    if (!text) throw new Error('Record what you concluded');
    if (text.length > CONCLUSION_MAX_LENGTH) throw new Error('The conclusion must be 4,000 characters or fewer');
    return this.gateway.closeInvestigation(investigationId, { status, conclusion: text });
  }

  async reopenInvestigation(investigationId) {
    return this.gateway.reopenInvestigation(investigationId);
  }

  async suggestedInvestigationEvidence(investigationId, options) {
    try {
      const result = await this.gateway.suggestedInvestigationEvidence(investigationId, options);
      return { items: Array.isArray(result?.items) ? result.items : [], note: result?.note || null };
    } catch (error) {
      if (this.#isUnavailable(error)) return { items: [], note: null };
      throw error;
    }
  }

  async attachInvestigationEvidence(investigationId, { evidenceId, note } = {}) {
    if (!evidenceId) throw new Error('Choose the evidence to gather');
    const text = String(note || '').trim();
    if (text.length > NOTE_MAX_LENGTH) throw new Error('The note must be 2,000 characters or fewer');
    return this.gateway.attachInvestigationEvidence(investigationId, { evidenceId, note: text || undefined });
  }

  async detachInvestigationEvidence(investigationId, evidenceId) {
    return this.gateway.detachInvestigationEvidence(investigationId, evidenceId);
  }

  // ── Hypotheses ────────────────────────────────────────────────────

  /** Proposals only; nothing is recorded until a member accepts one. */
  async generateHypotheses(investigationId) {
    try {
      const result = await this.gateway.generateHypotheses(investigationId);
      return {
        suggestions: Array.isArray(result?.suggestions) ? result.suggestions : [],
        note: result?.note || null,
      };
    } catch (error) {
      throw translateReasoningError(error);
    }
  }

  async createHypothesis(investigationId, statement) {
    const text = String(statement || '').trim();
    if (!text) throw new Error('Write the explanation');
    if (text.length > STATEMENT_MAX_LENGTH) throw new Error('The explanation must be 1,000 characters or fewer');
    return this.gateway.createHypothesis(investigationId, text);
  }

  async acceptHypothesisSuggestion(suggestionId, { statement } = {}) {
    const text = statement === undefined || statement === null ? undefined : String(statement).trim();
    if (text !== undefined) {
      if (!text) throw new Error('Write the explanation');
      if (text.length > STATEMENT_MAX_LENGTH) throw new Error('The explanation must be 1,000 characters or fewer');
    }
    return this.gateway.acceptHypothesisSuggestion(suggestionId, { statement: text });
  }

  async rejectHypothesisSuggestion(suggestionId, reason) {
    const text = String(reason || '').trim();
    if (text.length > REASON_MAX_LENGTH) throw new Error('The reason must be 1,000 characters or fewer');
    return this.gateway.rejectHypothesisSuggestion(suggestionId, text || undefined);
  }

  /**
   * What weakens a hypothesis. An empty result carries the server's sentence
   * verbatim; the view must render it rather than writing its own, because a
   * paraphrase is how "nothing found" becomes "confirmed".
   */
  async searchContradictions(hypothesisId) {
    try {
      const result = await this.gateway.searchContradictions(hypothesisId);
      return {
        candidates: Array.isArray(result?.candidates) ? result.candidates : [],
        nothingFoundNote: result?.nothingFoundNote || null,
      };
    } catch (error) {
      throw translateReasoningError(error);
    }
  }

  async attachHypothesisEvidence(hypothesisId, { evidenceId, stance, reason } = {}) {
    if (!evidenceId) throw new Error('Choose the evidence to attach');
    if (!STANCES.includes(stance)) throw new Error('Choose whether it supports or contradicts');
    const text = String(reason || '').trim();
    if (text.length > REASON_MAX_LENGTH) throw new Error('The reason must be 1,000 characters or fewer');
    return this.gateway.attachHypothesisEvidence(hypothesisId, { evidenceId, stance, reason: text || undefined });
  }

  async detachHypothesisEvidence(hypothesisId, evidenceId) {
    return this.gateway.detachHypothesisEvidence(hypothesisId, evidenceId);
  }

  async withdrawHypothesis(hypothesisId) {
    return this.gateway.withdrawHypothesis(hypothesisId);
  }

  // ── Constraints ───────────────────────────────────────────────────

  async listConstraints(filters) {
    try {
      const result = await this.gateway.listConstraints(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.constraints) ? result.constraints : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async getConstraint(constraintId) {
    return this.gateway.getConstraint(constraintId);
  }

  async recordConstraint(constraint = {}) {
    const name = String(constraint.name || '').trim();
    if (!name) throw new Error('A name is required');
    if (name.length > CONSTRAINT_NAME_MAX_LENGTH) throw new Error('The name must be 300 characters or fewer');
    if (!constraint.category) throw new Error('Choose a category');
    if (!constraint.scopeId && !constraint.sourceHypothesisId && !constraint.sourceDetectionId) {
      throw new Error('Choose a scope');
    }
    return this.gateway.recordConstraint({ ...constraint, name });
  }

  async updateConstraint(constraintId, changes = {}) {
    const next = { ...changes };
    if (Object.prototype.hasOwnProperty.call(next, 'name')) {
      next.name = String(next.name || '').trim();
      if (!next.name) throw new Error('A name is required');
    }
    if (next.status && !String(next.note || '').trim()) {
      throw new Error('Say why the status changed');
    }
    return this.gateway.updateConstraint(constraintId, next);
  }

  async linkConstraint(constraintId, link) {
    return this.gateway.linkConstraint(constraintId, link);
  }

  async unlinkConstraint(constraintId, linkId) {
    return this.gateway.unlinkConstraint(constraintId, linkId);
  }

  // ── Patterns ──────────────────────────────────────────────────────

  /** Detections and the catalogue, or null when they cannot be had. */
  async listPatterns(scopeId, options) {
    try {
      const result = await this.gateway.listPatterns(scopeId, options);
      if (!result || result.skipped) return null;
      return result;
    } catch (error) {
      if (this.#isUnavailable(error)) return null;
      throw error;
    }
  }

  async detectPatterns(scopeId) {
    return this.gateway.detectPatterns(scopeId);
  }

  async dismissDetection(detectionId, reason) {
    return this.gateway.dismissDetection(detectionId, reason);
  }

  async investigateDetection(detectionId) {
    return this.gateway.investigateDetection(detectionId);
  }

  // ── Interventions and their signals (spec 006) ─────────────────────

  /**
   * The list, or an empty one when the feature is unavailable. Never a throw
   * for that case: the Interventions pill can be reached before the session is
   * restored, and an exception there blanks the whole mode.
   */
  async listInterventions(filters) {
    try {
      const result = await this.gateway.listInterventions(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.interventions) ? result.interventions : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async getIntervention(interventionId) {
    return this.gateway.getIntervention(interventionId);
  }

  async recordIntervention(intervention = {}) {
    const problemStatement = String(intervention.problemStatement || '').trim();
    const proposedChange = String(intervention.proposedChange || '').trim();
    const ownerRole = String(intervention.ownerRole || '').trim();

    // A source supplies the problem statement, so it is only required when
    // recording one from nothing.
    const hasSource = Boolean(intervention.sourceHypothesisId || intervention.sourceConstraintId);
    if (!problemStatement && !hasSource) throw new Error('Say what the problem is');
    if (problemStatement.length > PROBLEM_STATEMENT_MAX_LENGTH) {
      throw new Error('The problem statement must be 4,000 characters or fewer');
    }
    if (!proposedChange) throw new Error('Say what you are going to do about it');
    if (proposedChange.length > PROPOSED_CHANGE_MAX_LENGTH) {
      throw new Error('The proposed change must be 4,000 characters or fewer');
    }
    if (ownerRole.length > OWNER_ROLE_MAX_LENGTH) {
      throw new Error('The owner role must be 200 characters or fewer');
    }
    if (!intervention.scopeId && !hasSource) throw new Error('Choose a scope');
    if (intervention.startDate && intervention.reviewDate
        && intervention.reviewDate < intervention.startDate) {
      throw new Error('The review date must be on or after the start date');
    }

    return this.gateway.recordIntervention({
      ...intervention,
      problemStatement,
      proposedChange,
      ownerRole: ownerRole || null,
    });
  }

  async updateIntervention(interventionId, changes = {}) {
    const next = { ...changes };
    for (const key of ['problemStatement', 'proposedChange', 'ownerRole', 'unintendedConsequences']) {
      if (!Object.prototype.hasOwnProperty.call(next, key)) continue;
      next[key] = String(next[key] || '').trim();
    }
    if (Object.prototype.hasOwnProperty.call(next, 'problemStatement') && !next.problemStatement) {
      throw new Error('Say what the problem is');
    }
    if (Object.prototype.hasOwnProperty.call(next, 'proposedChange') && !next.proposedChange) {
      throw new Error('Say what you are going to do about it');
    }
    if (next.status === 'reviewed') {
      throw new Error('Run the review instead — that is what records the outcome');
    }
    return this.gateway.updateIntervention(interventionId, next);
  }

  async attachInterventionEvidence(interventionId, link) {
    return this.gateway.attachInterventionEvidence(interventionId, link);
  }

  async detachInterventionEvidence(interventionId, evidenceId) {
    return this.gateway.detachInterventionEvidence(interventionId, evidenceId);
  }

  async addInterventionSignal(interventionId, signal = {}) {
    const name = String(signal.name || '').trim();
    if (!SIGNAL_KINDS.includes(signal.signalKind)) {
      throw new Error('Say whether this is something you expect to move or something to protect');
    }
    if (!SIGNAL_DIRECTIONS.includes(signal.direction)) throw new Error('Choose which way it should go');
    if (!name) throw new Error('Give the signal a name');
    if (name.length > SIGNAL_NAME_MAX_LENGTH) throw new Error('The name must be 200 characters or fewer');
    const unit = String(signal.unit || '').trim();
    if (unit.length > SIGNAL_UNIT_MAX_LENGTH) throw new Error('The unit must be 40 characters or fewer');
    const baselineValue = normaliseMeasurement(signal.baselineValue, 'The baseline');
    const targetValue = normaliseMeasurement(signal.targetValue, 'The target');

    return this.gateway.addInterventionSignal(interventionId, {
      ...signal, name, unit: unit || null, baselineValue, targetValue,
    });
  }

  async updateInterventionSignal(interventionId, signalId, changes = {}) {
    const next = { ...changes };
    if (Object.prototype.hasOwnProperty.call(next, 'name')) {
      next.name = String(next.name || '').trim();
      if (!next.name) throw new Error('Give the signal a name');
    }
    if (next.signalKind !== undefined && !SIGNAL_KINDS.includes(next.signalKind)) {
      throw new Error('Say whether this is something you expect to move or something to protect');
    }
    if (next.direction !== undefined && !SIGNAL_DIRECTIONS.includes(next.direction)) {
      throw new Error('Choose which way it should go');
    }
    if (Object.prototype.hasOwnProperty.call(next, 'baselineValue')) {
      next.baselineValue = normaliseMeasurement(next.baselineValue, 'The baseline');
    }
    if (Object.prototype.hasOwnProperty.call(next, 'targetValue')) {
      next.targetValue = normaliseMeasurement(next.targetValue, 'The target');
    }
    return this.gateway.updateInterventionSignal(interventionId, signalId, next);
  }

  async removeInterventionSignal(interventionId, signalId) {
    return this.gateway.removeInterventionSignal(interventionId, signalId);
  }

  // ── Readings and reviews (spec 006, US2) ───────────────────────────

  async recordSignalReading(interventionId, signalId, reading = {}) {
    // Required, not optional. An empty field silently becoming zero would be a
    // fabricated measurement in a table whose only job is to be believed.
    const value = normaliseMeasurement(reading.value, 'The value');
    if (value === null) throw new Error('Enter the value you measured');
    if (reading.periodStart && reading.periodEnd && reading.periodEnd < reading.periodStart) {
      throw new Error('The period end must be on or after the period start');
    }
    return this.gateway.recordSignalReading(interventionId, signalId, { ...reading, value });
  }

  async listSignalReadings(interventionId, signalId) {
    return this.gateway.listSignalReadings(interventionId, signalId);
  }

  /**
   * The comparison is arithmetic and comes back whether or not the model
   * answered, so a failure here is a failure of the request itself — not of
   * the review. The 409 and 503 translations are the ones the rest of the
   * reasoning endpoints use.
   */
  async runInterventionReview(interventionId) {
    try {
      return await this.gateway.runInterventionReview(interventionId);
    } catch (error) {
      throw translateReasoningError(error);
    }
  }

  async completeInterventionReview(reviewId, completion = {}) {
    const conclusion = String(completion.conclusion || '').trim();
    if (!REVIEW_OUTCOMES.includes(completion.outcome)) {
      throw new Error('Choose what happens next: continue, modify or stop');
    }
    if (!conclusion) throw new Error('Say what you concluded');
    if (conclusion.length > CONCLUSION_MAX_LENGTH) {
      throw new Error('The conclusion must be 4,000 characters or fewer');
    }
    if (completion.assessmentDisposition === 'edit'
        && !String(completion.assessmentMarkdown || '').trim()) {
      throw new Error('Write the assessment, or reject the draft instead');
    }
    return this.gateway.completeInterventionReview(reviewId, { ...completion, conclusion });
  }

  // ── Lessons (spec 006, US3) ────────────────────────────────────────

  async listLessons(filters) {
    try {
      const result = await this.gateway.listLessons(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.lessons) ? result.lessons : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async getLesson(lessonId) {
    return this.gateway.getLesson(lessonId);
  }

  async recordLesson(lesson = {}) {
    const statement = String(lesson.statement || '').trim();
    if (!statement) throw new Error('Say what you learned');
    if (statement.length > LESSON_STATEMENT_MAX_LENGTH) {
      throw new Error('The lesson must be 2,000 characters or fewer');
    }
    if (!LESSON_ORIGINS.includes(lesson.origin)) {
      throw new Error('A lesson is recorded from an intervention or from an investigation');
    }
    if (lesson.origin === 'intervention' && !lesson.sourceInterventionId) {
      throw new Error('Choose the intervention this came from');
    }
    if (lesson.origin === 'investigation' && !lesson.sourceInvestigationId) {
      throw new Error('Choose the investigation this came from');
    }
    return this.gateway.recordLesson({ ...lesson, statement });
  }

  // -- Memory recall (spec 006, US4) ----------------------------------

  /**
   * A recall reads across every kind of record. It stores nothing, so there is
   * no conversation to carry and nothing to clear afterwards.
   */
  async recallMemory({ question, scopeId } = {}) {
    const text = String(question || '').trim();
    if (!text) throw new Error('Ask a question');
    if (text.length > QUESTION_MAX_LENGTH) {
      throw new Error('The question must be 2,000 characters or fewer');
    }
    if (!scopeId) throw new Error('Choose a scope');
    try {
      return await this.gateway.recallMemory({ question: text, scopeId });
    } catch (error) {
      throw translateAskError(error);
    }
  }

  // -- Briefs (spec 006, US5) -----------------------------------------

  async listBriefs(filters) {
    try {
      const result = await this.gateway.listBriefs(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.briefs) ? result.briefs : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  /** A brief is stored as written, so reading one never regenerates anything. */
  async getBrief(briefId) {
    return this.gateway.getBrief(briefId);
  }

  async generateBrief({ scopeId, periodStart, periodEnd } = {}) {
    if (!scopeId) throw new Error('Choose a scope');
    if (!periodStart || !periodEnd) throw new Error('Choose the period this brief covers');
    if (periodEnd <= periodStart) throw new Error('The period end must be after the period start');
    const days = (new Date(periodEnd) - new Date(periodStart)) / 86400000;
    if (days > BRIEF_MAX_PERIOD_DAYS) {
      throw new Error('A brief covers at most a year');
    }
    try {
      return await this.gateway.generateBrief({ scopeId, periodStart, periodEnd });
    } catch (error) {
      throw translateReasoningError(error);
    }
  }

  // -- Connectors (spec 007) ------------------------------------------
  //
  // Every rule the server enforces is enforced here too, so a member gets a
  // sentence before a round trip rather than after one. The server is still
  // the authority: this is courtesy, not security.

  async listConnectors(filters) {
    try {
      const result = await this.gateway.listConnectors(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.connectors) ? result.connectors : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async getConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    try {
      return await this.gateway.getConnector(connectorId);
    } catch (error) {
      if (this.#isUnavailable(error)) return null;
      throw error;
    }
  }

  async createConnector(input = {}) {
    const connector = validateConnectorInput(input, true);
    return this.gateway.createConnector(connector);
  }

  /**
   * Change a connection.
   *
   * `sourceSystem` may be supplied and is used **only** to pick which fields
   * this source needs; it is never sent. A connection cannot change what it
   * connects to — that would be a different connection — but without knowing
   * which source it is, an update validates against Jira's rules and refuses
   * an AgileNotes connector for having no project key.
   */
  async updateConnector(connectorId, changes = {}) {
    if (!connectorId) throw new Error('connectorId is required');
    const { sourceSystem, ...rest } = changes;
    const named = validateConnectorInput({ ...rest, sourceSystem }, false);
    delete named.sourceSystem;
    if (Object.keys(named).length === 0) {
      throw new Error('Nothing to change');
    }
    return this.gateway.updateConnector(connectorId, named);
  }

  async testConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    return this.gateway.testConnector(connectorId);
  }

  async deleteConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    return this.gateway.deleteConnector(connectorId);
  }

  /**
   * Start a sync.
   *
   * A 409 means a sync is already running, which is ordinary rather than an
   * error -- two members can press the button at the same moment -- so it is
   * translated into a sentence rather than left as an HTTP status.
   */
  async runSync(connectorId, { fullRead = false } = {}) {
    if (!connectorId) throw new Error('connectorId is required');
    try {
      return await this.gateway.runSync(connectorId, { fullRead });
    } catch (error) {
      if (error?.status === 409) {
        throw new Error('A sync is already running for this connection.');
      }
      throw error;
    }
  }

  // -- Entity resolution (spec 007) -----------------------------------

  async listEntityResolutions(filters) {
    try {
      const result = await this.gateway.listEntityResolutions(filters);
      if (result?.skipped) return [];
      return Array.isArray(result?.resolutions) ? result.resolutions : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }

  async decideEntityResolution(resolutionId, decision) {
    if (!resolutionId) throw new Error('resolutionId is required');
    if (!RESOLUTION_DECISIONS.includes(decision)) {
      throw new Error('Choose confirm, reject, ignore or undo');
    }
    return this.gateway.decideEntityResolution(resolutionId, decision);
  }

  async createEntityFromMention(resolutionId, entity = {}) {
    if (!resolutionId) throw new Error('resolutionId is required');
    const name = String(entity.name ?? '').trim();
    if (!entity.entityType) throw new Error('Choose what kind of thing this is');
    if (!entity.homeScopeId) throw new Error('Choose which scope it belongs to');
    return this.gateway.createEntityFromMention(resolutionId, {
      entityType: entity.entityType,
      name: name || undefined,
      description: String(entity.description ?? '').trim() || undefined,
      homeScopeId: entity.homeScopeId,
    });
  }

  /**
   * Confirm a proposal into a scope.
   *
   * A parent is required rather than defaulted: a scope with no parent would be
   * a second root, and where a team sits in an organization is the member's
   * answer, not a source system's.
   */
  async createScopeFromMention(resolutionId, scope = {}) {
    if (!resolutionId) throw new Error('resolutionId is required');
    if (!scope.parentScopeId) throw new Error('Choose where this belongs in your organization');
    if (!scope.scopeType) throw new Error('Choose what kind of scope this is');
    return this.gateway.createScopeFromMention(resolutionId, {
      parentScopeId: scope.parentScopeId,
      scopeType: scope.scopeType,
      name: String(scope.name ?? '').trim() || undefined,
      description: String(scope.description ?? '').trim() || undefined,
    });
  }

  async listSyncRuns(connectorId, options) {
    if (!connectorId) throw new Error('connectorId is required');
    try {
      const result = await this.gateway.listSyncRuns(connectorId, options);
      return Array.isArray(result?.runs) ? result.runs : [];
    } catch (error) {
      if (this.#isUnavailable(error)) return [];
      throw error;
    }
  }
}

const CLOSING_STATUSES = ['answered', 'abandoned'];
const STANCES = ['supporting', 'contradicting'];
// Shorter than an Ask question: an investigation's question is a title people
// scan in a list, not something typed into a composer.
const INVESTIGATION_QUESTION_MAX_LENGTH = 500;
const CONCLUSION_MAX_LENGTH = 4000;
const NOTE_MAX_LENGTH = 2000;
const STATEMENT_MAX_LENGTH = 1000;
const REASON_MAX_LENGTH = 1000;
const CONSTRAINT_NAME_MAX_LENGTH = 300;
const PROBLEM_STATEMENT_MAX_LENGTH = 4000;
const PROPOSED_CHANGE_MAX_LENGTH = 4000;
const OWNER_ROLE_MAX_LENGTH = 200;
const SIGNAL_NAME_MAX_LENGTH = 200;
const SIGNAL_UNIT_MAX_LENGTH = 40;
const SIGNAL_KINDS = ['expected', 'guardrail'];
const SIGNAL_DIRECTIONS = ['decrease', 'increase', 'no_increase', 'no_decrease'];
const REVIEW_OUTCOMES = ['continue', 'modify', 'stop'];
const LESSON_ORIGINS = ['intervention', 'investigation'];
const LESSON_STATEMENT_MAX_LENGTH = 2000;
const BRIEF_MAX_PERIOD_DAYS = 366;
const RESOLUTION_DECISIONS = ['confirmed', 'rejected', 'ignored', 'undo'];

/** This machine, by any of its names. The server decides whether it is allowed. */
function isLoopbackAddress(value) {
  try {
    const { hostname } = new URL(value);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
      || hostname === '::1';
  } catch {
    return false;
  }
}

const CONNECTOR_NAME_MAX_LENGTH = 200;
/**
 * The sources a connection may name.
 *
 * A third copy of this list — the server has one and the presenter has
 * another — because the browser cannot import the server's. Slice 012 added
 * GitHub and GitLab and this copy was missed, so the form offered them and the
 * service refused them; a test now pins the two browser-side lists together.
 */
const CONNECTOR_SOURCE_SYSTEMS = [
  'jira', 'agilenotes', 'github', 'gitlab',
  'azure-devops', 'rally', 'agility', 'linear',
  'slack', 'teams', 'zoom',
  'pagerduty', 'opsgenie', 'incident-io', 'datadog', 'sentry',
  'grafana', 'new-relic', 'dynatrace',
  'confluence', 'notion', 'google-calendar', 'outlook-calendar',
  'webhook',
];

/**
 * What each source needs to be reachable, and the address it defaults to.
 *
 * AgileNotes asks for two fields where Jira asks for four: its token is
 * organization-scoped and carries no user identity, so there is no email to
 * pair with it and no project to name. Asking anyway would be asking somebody
 * to invent something their source does not have.
 */
const CONNECTOR_SOURCE_FIELDS = {
  jira: { required: ['baseUrl', 'projectKey'], optional: ['email'] },
  agilenotes: { required: ['baseUrl'], optional: [] },
  github: { required: ['baseUrl', 'repository'], optional: [] },
  gitlab: { required: ['baseUrl', 'projectPath'], optional: [] },
  'azure-devops': { required: ['baseUrl', 'organization', 'project'], optional: [] },
  rally: { required: ['baseUrl', 'project'], optional: [] },
  agility: { required: ['baseUrl', 'projectToken'], optional: [] },
  linear: { required: ['baseUrl', 'teamKey'], optional: [] },
  slack: { required: ['baseUrl', 'channels'], optional: [] },
  teams: { required: ['baseUrl', 'teamId', 'channels'], optional: [] },
  zoom: { required: ['baseUrl'], optional: [] },
  pagerduty: { required: ['baseUrl'], optional: [] },
  opsgenie: { required: ['baseUrl'], optional: [] },
  'incident-io': { required: ['baseUrl'], optional: [] },
  datadog: { required: ['baseUrl', 'applicationKey'], optional: [] },
  sentry: { required: ['baseUrl', 'organization', 'project'], optional: [] },
  // Objectives sources (spec 025).
  grafana: { required: ['baseUrl'], optional: [] },
  'new-relic': { required: ['baseUrl', 'accountId'], optional: [] },
  dynatrace: { required: ['baseUrl'], optional: [] },
  confluence: { required: ['baseUrl', 'spaceKey'], optional: ['email'] },
  notion: { required: ['baseUrl', 'databaseId'], optional: [] },
  'google-calendar': { required: ['baseUrl', 'calendarId'], optional: [] },
  'outlook-calendar': { required: ['baseUrl', 'calendarId'], optional: [] },
  // A webhook has nothing to reach, so it asks for nothing.
  webhook: { required: [], optional: [] },
};

const CONNECTOR_DEFAULT_BASE_URLS = {
  agilenotes: 'https://api.agilenotes.ai/v1/export',
  github: 'https://api.github.com',
  gitlab: 'https://gitlab.com',
  'azure-devops': 'https://dev.azure.com',
  rally: 'https://rally1.rallydev.com',
  linear: 'https://api.linear.app',
  slack: 'https://slack.com',
  teams: 'https://graph.microsoft.com',
  zoom: 'https://api.zoom.us',
  pagerduty: 'https://api.pagerduty.com',
  opsgenie: 'https://api.opsgenie.com',
  'incident-io': 'https://api.incident.io',
  datadog: 'https://api.datadoghq.com',
  sentry: 'https://sentry.io',
  notion: 'https://api.notion.com',
  'google-calendar': 'https://www.googleapis.com',
  'outlook-calendar': 'https://graph.microsoft.com',
  'new-relic': 'https://api.newrelic.com',
};
const MIN_AUTO_SYNC_MINUTES = 15;
const CONFIG_VALUE_MAX_LENGTH = 500;

/**
 * What a member typed about a connection, checked before it travels.
 *
 * When creating, everything required must be present. When updating, only the
 * fields actually named are checked and returned — so omitting the credential
 * leaves the stored one alone rather than blanking it, which matters because a
 * member cannot read back what they would have to retype.
 */
function validateConnectorInput(input, creating) {
  const has = (key) => Object.prototype.hasOwnProperty.call(input, key) && input[key] !== undefined;
  const named = {};

  if (creating || has('name')) {
    const name = String(input.name ?? '').trim();
    if (!name) throw new Error('Give the connection a name');
    if (name.length > CONNECTOR_NAME_MAX_LENGTH) {
      throw new Error('The name is too long');
    }
    named.name = name;
  }

  if (creating) {
    if (!CONNECTOR_SOURCE_SYSTEMS.includes(input.sourceSystem)) {
      throw new Error('Choose a source system');
    }
    named.sourceSystem = input.sourceSystem;
  }

  const credential = String(input.credential ?? '').trim();
  if (creating && !credential) throw new Error('Enter an API token');
  if (credential) named.credential = credential;

  if (creating || has('config')) {
    // `sourceSystem` is required when creating and carried for validation when
    // updating, so the field rules are always the ones this source actually
    // has rather than Jira's by default.
    named.config = validateConnectorConfig(input.sourceSystem, input.config);
  }

  if (creating || has('scopeId')) {
    if (!input.scopeId) throw new Error('Choose which scope this connection files evidence in');
    named.scopeId = input.scopeId;
  }

  if (creating || has('autoSyncEnabled') || has('autoSyncMinutes')) {
    const enabled = Boolean(input.autoSyncEnabled);
    if (enabled) {
      const minutes = Number.parseInt(input.autoSyncMinutes, 10);
      if (!Number.isFinite(minutes)) {
        throw new Error('Say how often to sync, in minutes');
      }
      if (minutes < MIN_AUTO_SYNC_MINUTES) {
        throw new Error('Automatic syncing runs at most every 15 minutes');
      }
      named.autoSyncMinutes = minutes;
    } else {
      named.autoSyncMinutes = null;
    }
    named.autoSyncEnabled = enabled;
  }

  return named;
}

/**
 * HTTPS is the rule of the whole slice, refused here as well as on the server
 * so a member who typed `http://` learns why without a round trip.
 *
 * A **loopback** address is passed through rather than refused. Not because it
 * is allowed — the server decides that, against an allowlist of exact origins
 * that is empty in production — but because this side cannot know the answer,
 * and a client that refuses what the server would accept produces an error
 * nobody can act on. No real Jira lives on localhost, so the fast, friendly
 * message still reaches everybody who needs it.
 */
function validateConnectorConfig(sourceSystem, config) {
  const source = config && typeof config === 'object' ? config : {};
  const fields = CONNECTOR_SOURCE_FIELDS[sourceSystem] || CONNECTOR_SOURCE_FIELDS.jira;
  const clean = {};

  for (const key of fields.required) {
    let value = String(source[key] ?? '').trim();
    if (!value && key === 'baseUrl') value = CONNECTOR_DEFAULT_BASE_URLS[sourceSystem] || '';
    if (!value) throw new Error(labelForConfigField(key, sourceSystem));
    if (value.length > CONFIG_VALUE_MAX_LENGTH) throw new Error('That value is too long');
    clean[key] = key === 'baseUrl' ? value.replace(/\/+$/, '') : value;
  }

  if (clean.baseUrl && !/^https:\/\//i.test(clean.baseUrl) && !isLoopbackAddress(clean.baseUrl)) {
    throw new Error('The address must start with https://');
  }

  for (const key of fields.optional) {
    const value = String(source[key] ?? '').trim();
    if (value) clean[key] = value;
  }

  return clean;
}

/** What to say when a field is missing, in the source's own words. */
const CONFIG_FIELD_LABELS = {
  projectKey: 'Enter the Jira project key',
  repository: 'Enter the repository, as owner/name',
  projectPath: 'Enter the project path, as group/project',
  organization: 'Enter your Azure DevOps organization',
  project: 'Enter the project',
  projectToken: 'Enter the Agility project (scope) token',
  teamKey: 'Enter the Linear team key',
  channels: 'Name the channels to read, separated by commas',
  teamId: 'Enter the Teams team id',
  applicationKey: 'Enter your Datadog application key',
  spaceKey: 'Name the Confluence space to read',
  databaseId: 'Name the Notion database to read',
  calendarId: 'Name the calendar to read',
};

const BASE_URL_LABELS = {
  jira: 'Enter the address of your Jira site',
  agilenotes: 'Enter the address of your AgileNotes export API',
  github: 'Enter the address of your GitHub API',
  gitlab: 'Enter the address of your GitLab',
  'azure-devops': 'Enter the address of your Azure DevOps',
  rally: 'Enter the address of your Rally',
  agility: 'Enter the address of your Agility instance',
  linear: 'Enter the address of the Linear API',
  slack: 'Enter the address of the Slack API',
  teams: 'Enter the address of the Microsoft Graph API',
  zoom: 'Enter the address of the Zoom API',
  pagerduty: 'Enter the address of the PagerDuty API',
  opsgenie: 'Enter the address of the Opsgenie API',
  'incident-io': 'Enter the address of the incident.io API',
  datadog: 'Enter the address of your Datadog site',
  sentry: 'Enter the address of your Sentry',
  confluence: 'Enter the address of your Confluence site',
  notion: 'Enter the address of the Notion API',
  'google-calendar': 'Enter the address of the Google Calendar API',
  'outlook-calendar': 'Enter the address of the Microsoft Graph API',
};

function labelForConfigField(key, sourceSystem) {
  if (CONFIG_FIELD_LABELS[key]) return CONFIG_FIELD_LABELS[key];
  if (key === 'baseUrl') return BASE_URL_LABELS[sourceSystem] || BASE_URL_LABELS.jira;
  return `Enter the ${key}`;
}


/**
 * A measurement a member typed. An empty field is nothing, not zero — a signal
 * with no baseline is unmeasured, and recording it as zero would make every
 * later comparison a lie. Anything that is not a decimal is refused by name.
 *
 * The value is kept as a string all the way to the API, because the column is
 * NUMERIC and 8.4 must arrive as 8.4.
 */
function normaliseMeasurement(value, label) {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).trim();
  if (!/^[+-]?\d+(\.\d+)?$/.test(text)) throw new Error(label + ' must be a number');
  return text;
}

/**
 * The reasoning endpoints answer 409 busy and 503 unavailable, as Ask does.
 * The controller acts on the code, never on the wording.
 */
function translateReasoningError(error) {
  if (error?.status === 409 && error.message === 'busy') {
    const typed = new Error('That is already being worked on');
    typed.code = 'busy';
    typed.status = 409;
    return typed;
  }
  if (error?.status === 503) {
    const typed = new Error(error.message || 'Suggestions are unavailable right now');
    typed.code = 'unavailable';
    typed.status = 503;
    return typed;
  }
  return error;
}

const QUESTION_MAX_LENGTH = 2000;
const CONVERSATION_TITLE_MAX_LENGTH = 300;

/** The API's status answers as errors with a code the controller can switch on. */
function translateAskError(error) {
  if (error?.status === 409 && error.message === 'scope_changed') {
    const typed = new Error('This conversation belongs to another scope');
    typed.code = 'scope_changed';
    typed.status = 409;
    typed.conversationScopeId = error.body?.conversationScopeId || null;
    typed.conversationScopeName = error.body?.conversationScopeName || null;
    return typed;
  }
  if (error?.status === 409 && error.message === 'busy') {
    const typed = new Error('An answer for this conversation is already on its way');
    typed.code = 'busy';
    typed.status = 409;
    return typed;
  }
  if (error?.status === 503) {
    const typed = new Error('Answers are unavailable right now');
    typed.code = 'unavailable';
    typed.status = 503;
    return typed;
  }
  return error;
}

if (typeof window !== 'undefined') {
  window.AgileProServices = window.AgileProServices || {};
  window.AgileProServices.IntelligenceService = IntelligenceService;
}
